/**
 * LocationSuggestBanner — silent city guess with accept / change / refine.
 *
 * On first visit (no `sp_home_city` in localStorage):
 *   1. GET /api/geo/ip → soft city guess from IP (no permission prompt)
 *   2. Show "We think you're in {City}. Is this right?" with three actions:
 *        • Yes, use this   → save to sp_home_city
 *        • Not quite       → open manual picker (/local)
 *        • Use precise     → navigator.geolocation → /api/geo/reverse → save
 *
 * The banner never re-appears once the user picks (accept, refine, or
 * dismiss) — stored via `sp_location_prompted` so we don't nag returning
 * readers.
 */
import { useCallback, useEffect, useState } from "react";
import { MapPin, Check, X, Locate, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CITY_KEY   = "sp_home_city";
const NP_KEY     = "np_home_city";   // shared bucket w/ NewsPulse
const PROMPT_KEY = "sp_location_prompted";

function saveCity(city) {
  try {
    const payload = JSON.stringify(city);
    window.localStorage.setItem(CITY_KEY, payload);
    // Mirror to NewsPulse's bucket so the whole network stays consistent
    if (!window.localStorage.getItem(NP_KEY)) {
      window.localStorage.setItem(NP_KEY, payload);
    }
    // Notify same-tab listeners (localStorage 'storage' event fires on
    // OTHER tabs only). BlogHome uses this to re-personalize on the fly.
    try { window.dispatchEvent(new CustomEvent("sp:home-city-changed", { detail: city })); } catch { /* noop */ }
  } catch { /* noop */ }
}

export default function LocationSuggestBanner({ hasHomeCity, onAccepted, onDismissed, onChange }) {
  const [suggest, setSuggest] = useState(null);   // {city,state,country,latitude,longitude}
  const [state,   setState]   = useState("idle"); // idle | probing | ready | precise-loading | saved
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (hasHomeCity) return;
    try {
      if (window.localStorage.getItem(PROMPT_KEY) === "1") return;
    } catch { /* noop */ }
    let alive = true;
    setState("probing");
    (async () => {
      try {
        const r = await fetch(`${API}/geo/ip`);
        const d = await r.json();
        if (!alive) return;
        if (d && d.ok && d.city) {
          setSuggest(d);
          setState("ready");
        } else {
          setState("idle");
        }
      } catch {
        if (alive) setState("idle");
      }
    })();
    return () => { alive = false; };
  }, [hasHomeCity]);

  const markPrompted = () => {
    try { window.localStorage.setItem(PROMPT_KEY, "1"); } catch { /* noop */ }
  };

  const acceptSuggest = useCallback(() => {
    if (!suggest) return;
    const city = {
      city:         suggest.city,
      state:        suggest.state,
      country:      suggest.country,
      country_code: suggest.country_code,
      latitude:     suggest.latitude,
      longitude:    suggest.longitude,
      source:       "ip",
    };
    saveCity(city);
    markPrompted();
    setState("saved");
    onAccepted?.(city);
  }, [suggest, onAccepted]);

  const usePrecise = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setError("Your browser doesn't support precise location.");
      return;
    }
    setState("precise-loading");
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await fetch(`${API}/geo/reverse`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              latitude:  pos.coords.latitude,
              longitude: pos.coords.longitude,
            }),
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const d = await r.json();
          const city = {
            city:         d.city,
            state:        d.state,
            country:      d.country,
            country_code: d.country_code,
            latitude:     pos.coords.latitude,
            longitude:    pos.coords.longitude,
            source:       "gps",
          };
          saveCity(city);
          markPrompted();
          setState("saved");
          onAccepted?.(city);
        } catch (e) {
          setError("Couldn't resolve your precise location — try 'Change ›' to pick manually.");
          setState("ready");
        }
      },
      (err) => {
        setError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied — you can still pick manually."
            : "Couldn't read your location — try 'Change ›' to pick manually.",
        );
        setState("ready");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300_000 },
    );
  }, [onAccepted]);

  const dismiss = useCallback(() => {
    markPrompted();
    setState("saved");
    onDismissed?.();
  }, [onDismissed]);

  const change = useCallback(() => {
    markPrompted();
    onChange?.();
  }, [onChange]);

  // Nothing to show — either user already has a city, or we couldn't guess
  if (hasHomeCity || state === "idle" || state === "probing" || state === "saved" || !suggest) {
    return null;
  }

  const cityLabel = [suggest.city, suggest.state, suggest.country].filter(Boolean).join(", ");

  return (
    <div
      className="max-w-[1400px] mx-auto mx-6 md:mx-10 my-2 rounded-xl border bg-white/70 backdrop-blur px-4 py-2.5 flex flex-wrap items-center gap-2 shadow-sm"
      style={{ borderColor: "rgba(15,118,110,0.18)" }}
      data-testid="location-suggest-banner"
    >
      <div
        className="w-8 h-8 rounded-full grid place-items-center shrink-0"
        style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}
        aria-hidden="true"
      >
        <MapPin className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm text-slate-800">
          We think you're near <strong className="text-slate-950">{cityLabel}</strong>.{" "}
          <span className="text-slate-500 text-xs">Change or refine below.</span>
        </div>
        {error && (
          <div className="text-xs text-amber-700 mt-0.5" data-testid="location-suggest-error">
            {error}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={acceptSuggest}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-xs font-semibold hover:opacity-90 transition"
          style={{ background: "#0F766E" }}
          data-testid="location-suggest-accept"
        >
          <Check className="w-3.5 h-3.5" /> Yes, use this
        </button>
        <button
          type="button"
          onClick={usePrecise}
          disabled={state === "precise-loading"}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 text-slate-800 text-xs font-semibold hover:bg-slate-200 transition disabled:opacity-50"
          data-testid="location-suggest-precise"
        >
          {state === "precise-loading" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Locate className="w-3.5 h-3.5" />
          )}
          Use precise
        </button>
        <button
          type="button"
          onClick={change}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-50 text-slate-700 text-xs font-semibold hover:bg-slate-100 transition"
          data-testid="location-suggest-change"
        >
          Not quite ›
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="w-7 h-7 rounded-full grid place-items-center hover:bg-slate-100 text-slate-400"
          aria-label="Dismiss location suggestion"
          data-testid="location-suggest-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
